import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import { PageCard } from '../../../Compounds/CardWithTitle/PageCard';
import { Icon } from '../../../Atoms/Icon';
import { TextSpan } from '../../../Atoms/Text';
import { DEFAULT_ADVANCE_BUTTON_STYLE, DEFAULT_CANCEL_BUTTON_STYLE } from '../../../../utils/styleHelpers';
import { useSnackbar } from '../../../../Context/SnackbarContext';
import { usePermissionSetAssignments, usePermissionSets } from '../../../../store/PermissionSets';
import { getTeamsSubject } from '../../../../store/Team';
import { PermissionSetsList } from '../../../Compounds/PermissionSets/PermissionSetsList';
import { PermissionSetEditor } from '../../../Compounds/PermissionSets/PermissionSetEditor';
import { PermissionSetDetail } from '../../../Compounds/PermissionSets/PermissionSetDetail';
import { PermissionSet } from '../../../../types';

export const PermissionSetsTab = () => {
  const { setSnack } = useSnackbar();
  const { permissionSets, loading, error, submitting, deletePermissionSet, loadPermissionSets } = usePermissionSets();
  const { assignments } = usePermissionSetAssignments();
  const [teams, setTeams] = useState(getTeamsSubject().getValue());
  const [creating, setCreating] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<PermissionSet | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const detailId = searchParams.get('detailId');
  const detailSet = detailId ? permissionSets.find((set) => set.id === detailId) : undefined;

  useEffect(() => {
    const sub = getTeamsSubject().subscribe(setTeams);
    return () => sub.unsubscribe();
  }, []);

  const teamMemberCount = (teamId: number): number => teams.find((team) => team.id === String(teamId))?.user_count ?? 0;

  const closeDetail = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('detailId');
    setSearchParams(next, { replace: true });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    const result = await deletePermissionSet(deleteTarget.id);
    if (result.error) {
      setSnack({ message: result.error.message || 'Could not delete permission set', type: 'error', open: true });
      return;
    }
    setSnack({ message: `Deleted ${deleteTarget.name}`, type: 'success', open: true });
    setDeleteTarget(null);
  };

  const showList = !creating && !detailSet;

  return (
    <PageCard
      title="Permission Sets"
      dataCy="permission-sets-tab"
      enableCollapse={false}
      cardActions={
        showList ? (
          <button
            type="button"
            className="flex space-x-2 items-center"
            data-cy="new-permission-set"
            onClick={() => setCreating(true)}>
            <Icon
              type="plus"
              className="p-2"
            />
            <TextSpan
              color="grey12"
              size="sm14">
              New Permission Set
            </TextSpan>
          </button>
        ) : undefined
      }
      childrenWrapperClass="min-h-80 max-w-[90vw] min-w-full">
      {creating && (
        <PermissionSetEditor
          mode="create"
          permissionSet={null}
          onCancel={() => setCreating(false)}
          onSaved={() => setCreating(false)}
        />
      )}

      {detailSet && !creating && (
        <PermissionSetDetail
          permissionSet={detailSet}
          onBack={closeDetail}
        />
      )}

      {showList && (
        <PermissionSetsList
          permissionSets={permissionSets}
          loading={loading}
          error={error}
          assignments={assignments}
          teamMemberCount={teamMemberCount}
          onDelete={(permissionSet) => setDeleteTarget(permissionSet)}
          onRetry={() => loadPermissionSets(true)}
        />
      )}

      <Dialog
        open={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete permission set</DialogTitle>
        <DialogContent>
          <Typography>
            Delete “{deleteTarget?.name}”? Users and teams assigned to it will fall back to their role.
          </Typography>
        </DialogContent>
        <DialogActions>
          <button
            type="button"
            className={DEFAULT_CANCEL_BUTTON_STYLE}
            disabled={submitting}
            onClick={() => setDeleteTarget(null)}>
            Cancel
          </button>
          <button
            type="button"
            className={DEFAULT_ADVANCE_BUTTON_STYLE}
            disabled={submitting}
            onClick={handleDeleteConfirm}>
            Delete
          </button>
        </DialogActions>
      </Dialog>
    </PageCard>
  );
};
